import * as React from 'react';
import { useCallback, type FC } from 'react';
import { useDispatch } from 'react-redux';
import { twJoin } from 'tailwind-merge';

import { Span } from '@/core/tamagoshiTailwind/components/Typography/Typography';
import { MenuIcon } from '@/core/tamagoshiTailwind/icons/MenuIcon';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { setMenuVisibility } from '@/domains/productDiscovery/HeaderLeonidas/HeaderLeonidas.actions';
import { useMenuVisibility } from '@/domains/productDiscovery/HeaderLeonidas/hooks/useMenuVisibility';
import { GTM_CLICK_ON_MENU_BUTTON } from '@/domains/productDiscovery/HeaderLeonidas/services/tracking/gtmEvents';

import { leonidasMenuButton } from './translations';

interface MenuButtonProps {
  className?: string;
}

export const MenuButton: FC<MenuButtonProps> = React.memo(
  ({ className, ...otherProps }) => {
    const dispatch = useDispatch();
    const isMenuVisible = useMenuVisibility();

    const handleOnClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (!isMenuVisible) {
          Gtm.push(GTM_CLICK_ON_MENU_BUTTON());
        }
        dispatch(setMenuVisibility(!isMenuVisible));
      },
      [dispatch, isMenuVisible],
    );

    const dataMenuVisible = Boolean(isMenuVisible).toString();

    return (
      <button
        title={leonidasMenuButton}
        type="button"
        className={twJoin(
          `max-w-[40px]background-none block min-h-[44px]
          text-foundation-inverted desktop:relative desktop:mr-s desktop:flex
          desktop:h-[24px] desktop:min-h-[24px] desktop:max-w-[92px]
          desktop:flex-nowrap desktop:items-center desktop:justify-center
          desktop:gap-x-xs desktop:overflow-hidden desktop:px-s`,
          className,
        )}
        data-menu-visible={dataMenuVisible}
        data-testid="MM-menu"
        onClick={handleOnClick}
        {...otherProps}
      >
        <MenuIcon />
        <Span scale="body2" weight="semibold" className="hidden desktop:block">
          {leonidasMenuButton}
        </Span>
      </button>
    );
  },
);

MenuButton.displayName = 'MenuButton';
